// ✅ src/pages/VehicleV2/EquipmentListing.jsx

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";

import { FaPlus, FaSearch, FaFilter, FaEdit, FaTrash, FaList } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/equipmentListing";

const toInt = (v, d = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : d;
};

const EquipmentListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  // ✅ localStorage values (same style as your attached code)
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const branchId = localStorage.getItem("branchId") || "";
  const branch = localStorage.getItem("branchType") || "";
  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginUserId") ||
    localStorage.getItem("loginuserid") ||
    "";

  // filters
  const [search, setSearch] = useState("");
  const [showFilters, setShowFilters] = useState(true);

  // list + paging
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / limit)), [total, limit]);

  // rights
  const branchActive = String(
    localStorage.getItem("BranchActive") ||
      sessionStorage.getItem("BranchActive") ||
      "1"
  ) === "1";

  const [rights] = useState({
    canAccess: true,
    canAdd: branchActive,
    canDelete: branchActive,
    canEdit: branchActive,
  });

  // selection
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branch, userId]
  );

  const normalizeRow = (row, idx = 0) => {
    const equipmentId =
      row?.fld_equipmentid ||
      row?.equipmentId ||
      row?.EquipmentId ||
      row?.id ||
      0;

    const equipmentType =
      row?.fld_equipmenttype ||
      row?.equipmentType ||
      row?.EquipmentType ||
      row?.name ||
      row?.["Vehicle Type"] ||
      "-";

    return {
      ...row,
      equipmentId,
      equipmentType,
      slno: row?.slno || idx + 1,
    };
  };

  const loadList = async (options = {}) => {
    const nextPage = options.page ?? page;
    const nextLimit = options.limit ?? limit;
    const nextSearch = options.search ?? search;

    setLoading(true);
    try {
      const r = await axios.get(`${API_BASE}/list`, {
        params: {
          search: nextSearch || "",
          page: nextPage,
          limit: nextLimit,
        },
        headers: commonHeaders,
      });

      if (r.data?.success === false) {
        throw new Error(r.data?.message || "Failed to load list");
      }

      const list = Array.isArray(r.data?.rows)
        ? r.data.rows
        : Array.isArray(r.data?.data)
          ? r.data.data
          : Array.isArray(r.data?.list)
            ? r.data.list
            : [];

      const normalized = list.map((item, idx) => normalizeRow(item, idx));
      const totalCount = toInt(r.data?.total, normalized.length);

      setRows(normalized);
      setTotal(totalCount);
      setSelectedIds([]);
      setSelectAll(false);
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || err?.message || "Failed to load equipment list");
      setRows([]);
      setTotal(0);
      setSelectedIds([]);
      setSelectAll(false);
    } finally {
      setLoading(false);
    }
  };

  const loadViewAll = async () => {
    setPage(1);
    setSearch("");
    await loadList({ page: 1, search: "", limit });
  };

  const onSearch = async () => {
    setPage(1);
    await loadList({ page: 1, search });
  };

  const clearFilters = async () => {
    setSearch("");
    setPage(1);
    await loadList({ page: 1, search: "" });
  };

  const handleAddNew = () => {
    sessionStorage.removeItem("EquipID");
    navigate("/vehicle/Equipment");
  };

  const handleEdit = (row) => {
    sessionStorage.setItem("EquipID", String(row.equipmentId || ""));
    navigate("/vehicle/Equipment");
  };

  const toggleSelect = (equipId) => {
    const id = String(equipId);

    setSelectedIds((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];

      const allIds = rows.map((r) => String(r.equipmentId));
      setSelectAll(allIds.length > 0 && next.length === allIds.length);

      return next;
    });
  };

  const handleSelectAll = () => {
    const allIds = rows.map((r) => String(r.equipmentId));

    if (selectAll) {
      setSelectedIds([]);
      setSelectAll(false);
    } else {
      setSelectedIds(allIds);
      setSelectAll(true);
    }
  };

  // ASPX-like page move block when selected rows exist
  const canMovePage = () => {
    if (selectedIds.length > 0) {
      toast.warn("Please delete the selected record(s) in current page and proceed to next page");
      return false;
    }
    return true;
  };

  const gotoPage = async (p) => {
    if (!canMovePage()) return;
    const next = Math.min(Math.max(1, p), totalPages);
    setPage(next);
  };

  const handleDeleteSelected = async () => {
    if (!rights.canDelete) return;

    if (selectedIds.length === 0) {
      toast.warn("Please check the selection");
      return;
    }

    const ok = window.confirm("Are you sure to delete?");
    if (!ok) {
      setSelectedIds([]);
      setSelectAll(false);
      return;
    }

    try {
      const r = await axios.post(
        `${API_BASE}/delete`,
        {
          ids: selectedIds.map((x) => toInt(x)),
          loginUserId: toInt(userId),
        },
        {
          headers: commonHeaders,
        }
      );

      const deletedCount = toInt(r.data?.deletedCount, 0);
      const msg =
        r.data?.message ||
        (deletedCount === selectedIds.length
          ? "Deleted Successfully"
          : deletedCount > 0
            ? "Equipment(s) in use cannot be removed, others Removed successfully"
            : "Equipment(s) in use cannot be deleted");

      if (r.data?.success === false) {
        toast.error(msg);
      } else if (deletedCount > 0 && deletedCount < selectedIds.length) {
        toast.warn(msg);
      } else if (deletedCount > 0) {
        toast.success(msg);
      } else {
        toast.warn(msg);
      }

      const nextTotal = Math.max(0, total - deletedCount);
      const nextPages = Math.max(1, Math.ceil(nextTotal / limit));
      const nextPage = Math.min(page, nextPages);

      setPage(nextPage);
      await loadList({ page: nextPage });
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Delete failed");
    }
  };

  // ---------- Initial load ----------
  useEffect(() => {
    loadList({ page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  // ---------- page/limit change ----------
  useEffect(() => {
    loadList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, limit]);

  if (!rights.canAccess) {
    return (
      <VehicleLayoutWrapper>
        <div className="p-6">
          <div className="p-4 rounded-lg border border-red-300 bg-red-50 text-red-700">
            You don’t have rights to access this page.
          </div>
        </div>
      </VehicleLayoutWrapper>
    );
  }

  return (
    <VehicleLayoutWrapper>
      <HeaderBar
        title={<span className="font-dmserif text-3xl">EQUIPMENT LISTING</span>}
        setSkin={setSkin}
        currentSkin={skin}
      />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        {/* Top bar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-4">
            <h2 className={`text-xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Equipment Listing
            </h2>

            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
              }`}
            >
              {loading ? "Loading..." : `${total} Records`}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-800" +
                    (showFilters ? " bg-gray-800" : "")
                  : "border-gray-300 text-gray-700 hover:bg-gray-50" +
                    (showFilters ? " bg-gray-50" : "")
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            {rights.canAdd && (
              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors`}
              >
                <FaPlus className="w-4 h-4" />
                Add New
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div
            className={`rounded-xl p-5 border mb-6 ${
              isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
            }`}
          >
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2">
                <label
                  className={`block text-sm font-medium mb-1 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Search Equipment
                </label>

                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaSearch className="text-gray-400" />
                  </div>

                  <input
                    type="text"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") onSearch();
                    }}
                    placeholder="Type to search equipment..."
                    className={`w-full pl-10 px-4 py-2 rounded-lg border ${
                      isDark
                        ? "bg-gray-700 border-gray-600 text-white"
                        : "bg-white border-gray-300 text-gray-900"
                    }`}
                  />
                </div>
              </div>

              <div className="flex items-end gap-3">
                <button
                  onClick={onSearch}
                  className={`flex items-center gap-2 px-4 py-2 rounded text-white ${buttonClass} ${hoverClass}`}
                >
                  <FaSearch /> Search
                </button>

                <button
                  onClick={loadViewAll}
                  className="flex items-center gap-2 px-4 py-2 rounded border border-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  <FaList /> View All
                </button>

                <button
                  onClick={clearFilters}
                  className="px-4 py-2 rounded border border-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Table */}
        <div
          className={`rounded-xl shadow-sm border overflow-hidden ${
            isDark ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className={`${buttonClass} text-white`}>
                <tr>
                  <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">S.No</th>
                  <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">
                    Vehicle Type
                  </th>
                  <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">Edit</th>
                  <th className="px-4 py-4 text-center font-semibold whitespace-nowrap">
                    <div className="flex items-center justify-center gap-2">
                      <span>Select All</span>
                      <input
                        type="checkbox"
                        checked={selectAll}
                        onChange={handleSelectAll}
                        disabled={!rights.canDelete || rows.length === 0}
                      />
                    </div>
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading ? (
                  Array.from({ length: 5 }).map((_, idx) => (
                    <tr key={idx} className={isDark ? "bg-gray-800" : "bg-white"}>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-10 mx-auto" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-40 mx-auto" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-8 bg-gray-300 dark:bg-gray-600 rounded animate-pulse w-20 mx-auto" />
                      </td>
                      <td className="px-4 py-3">
                        <div className="h-4 w-4 bg-gray-300 dark:bg-gray-600 rounded mx-auto animate-pulse" />
                      </td>
                    </tr>
                  ))
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-4 py-8 text-center text-gray-500">
                      No records found.
                    </td>
                  </tr>
                ) : (
                  rows.map((row, idx) => (
                    <tr
                      key={`${row.equipmentId}_${idx}`}
                      className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200 ${
                        isDark ? "bg-gray-900" : "bg-white"
                      }`}
                    >
                      <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-white text-center">
                        {row.slno || idx + 1}
                      </td>

                      <td className="px-4 py-3 text-center">
                        <span className="text-blue-600 dark:text-blue-400 font-medium">
                          {row.equipmentType}
                        </span>
                      </td>

                      <td className="px-4 py-3 text-center">
                        <button
                          onClick={() => handleEdit(row)}
                          disabled={!rights.canEdit}
                          className={`px-3 py-1 rounded text-xs font-medium ${
                            isDark
                              ? "bg-blue-600 hover:bg-blue-700 text-white"
                              : "bg-blue-100 hover:bg-blue-200 text-blue-700"
                          }`}
                        >
                          <FaEdit className="inline mr-1" />
                          Edit
                        </button>
                      </td>

                      <td className="px-4 py-3 text-center">
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(String(row.equipmentId))}
                          onChange={() => toggleSelect(row.equipmentId)}
                          disabled={!rights.canDelete}
                        />
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer actions */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mt-6">
          {/* Pagination */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => gotoPage(page - 1)}
              disabled={page <= 1}
              className={`px-3 py-2 rounded border ${
                isDark ? "border-gray-700 text-white" : "border-gray-300 text-gray-800"
              } ${
                page <= 1
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-gray-50 dark:hover:bg-gray-800"
              }`}
            >
              Prev
            </button>

            <div className={`text-sm ${isDark ? "text-gray-200" : "text-gray-700"}`}>
              Page <b>{page}</b> / <b>{totalPages}</b>
            </div>

            <button
              onClick={() => gotoPage(page + 1)}
              disabled={page >= totalPages}
              className={`px-3 py-2 rounded border ${
                isDark ? "border-gray-700 text-white" : "border-gray-300 text-gray-800"
              } ${
                page >= totalPages
                  ? "opacity-50 cursor-not-allowed"
                  : "hover:bg-gray-50 dark:hover:bg-gray-800"
              }`}
            >
              Next
            </button>

            <select
              value={limit}
              onChange={(e) => {
                if (!canMovePage()) return;
                setLimit(toInt(e.target.value, 10));
                setPage(1);
              }}
              className={`ml-2 px-3 py-2 rounded border ${
                isDark
                  ? "bg-gray-900 border-gray-700 text-white"
                  : "bg-white border-gray-300 text-gray-900"
              }`}
            >
              {[10, 20, 50].map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </select>
          </div>

          {/* Delete */}
          {rights.canDelete && (
            <div className="flex justify-end">
              <button
                onClick={handleDeleteSelected}
                disabled={selectedIds.length === 0}
                className={`flex items-center gap-2 px-4 py-2 text-white text-sm rounded-lg shadow transition ${
                  selectedIds.length === 0
                    ? "bg-gray-400 cursor-not-allowed"
                    : `${buttonClass} ${hoverClass}`
                }`}
              >
                <FaTrash /> Delete Selected
              </button>
            </div>
          )}
        </div>

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          closeOnClick
          pauseOnHover
          theme={isDark ? "dark" : "light"}
        />
      </div>
    </VehicleLayoutWrapper>
  );
};

export default EquipmentListing;